import React from 'react';
import {Box, Typography, Card, CardContent, Chip} from '@mui/material';
import {Link} from 'react-router-dom';
import '../Demo.scss';

interface ApiEndpoint {
  path: string;
  label: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  endpoint: string;
  description: string;
  status: 'available' | 'coming-soon';
}

const DemoApiProductionList: React.FC = () => {
  const apiEndpoints: ApiEndpoint[] = [
    {
      path: '/demoproductionapi/document-selection-view',
      label: 'Document Selection View',
      method: 'GET',
      endpoint: '/productions/api/v1/production/view/documentselectionview',
      description:
        'Get document selection view (folders, tags, saved searches, counts, and context selections)',
      status: 'available',
    },
    {
      path: '/demoproductionapi/save-documents-selection',
      label: 'Save Documents Selection',
      method: 'POST',
      endpoint: '/productions/api/v1/production/documents-selection',
      description:
        'Save selected documents context (folders/tags/saved searches) for a production',
      status: 'available',
    },
    {
      path: '/demoproductionapi/production-guard-view',
      label: 'Production Guard View',
      method: 'GET',
      endpoint: '/productions/api/v1/production/view/productionguardview',
      description:
        'Get production guard view with metadata details, production fields, tag lists, and privilege document settings',
      status: 'available',
    },
    {
      path: '/demoproductionapi/save-production-guard-view',
      label: 'Save Production Guard View',
      method: 'POST',
      endpoint: '/productions/api/v1/production/guard',
      description:
        'Save production guard view including guard queries, privilege document settings, and document counts',
      status: 'available',
    },
    {
      path: '/demoproductionapi/production-guard-match-results',
      label: 'Production Guard Match Results',
      method: 'POST',
      endpoint: '/productions/api/v1/production/guard/match-results',
      description:
        'Get production guard match results including document counts, navigation buttons, and privilege document information',
      status: 'available',
    },
    {
      path: '/demoproductionapi/production-components',
      label: 'Production Components (All)',
      method: 'GET',
      endpoint: '/productions/api/v1/production/view/productioncomponents',
      description:
        'Get complete production configuration including all components (DAT, Native, Text, TIFF, PDF, MP3, Translation, Archive)',
      status: 'available',
    },
    {
      path: '/demoproductionapi/template-components',
      label: 'Template Components',
      method: 'GET',
      endpoint: '/productions/api/v1/production/templates/components-details',
      description:
        'Get complete component configuration for a production template (DAT, Native, Text, TIFF, PDF, MP3, Translation, Archive)',
      status: 'available',
    },
    {
      path: '/demoproductionapi/save-production-components',
      label: 'Save Production Components',
      method: 'POST',
      endpoint: '/productions/api/v1/production/components',
      description:
        'Save production component configuration with selected components (DAT, Native, Text, TIFF, PDF, MP3, Translation, Archive)',
      status: 'available',
    },
    {
      path: '/demoproductionapi/dat-component',
      label: 'DAT Component',
      method: 'GET',
      endpoint: '/productions/api/v1/production/view/datcomponent',
      description:
        'Get DAT component configuration including ANSI formats, delimiters, and field mappings',
      status: 'available',
    },
    {
      path: '/demoproductionapi/native-component',
      label: 'Native Component',
      method: 'GET',
      endpoint: '/productions/api/v1/production/view/nativecomponent',
      description:
        'Get native component configuration including file types, load file settings, and HTML email generation options',
      status: 'available',
    },
    {
      path: '/demoproductionapi/text-component',
      label: 'Text Component',
      method: 'GET',
      endpoint: '/productions/api/v1/production/view/textcomponent',
      description:
        'Get text component configuration including ANSI types, text format, OCR settings, and TIFF matching options',
      status: 'available',
    },
    {
      path: '/demoproductionapi/mp3-file-component',
      label: 'MP3 File Component',
      method: 'GET',
      endpoint: '/productions/api/v1/production/view/mp3filecomponent',
      description:
        'Get MP3 file component configuration including redaction settings, filler audio, and load file options',
      status: 'available',
    },
    {
      path: '/demoproductionapi/translation-component',
      label: 'Translation Component',
      method: 'GET',
      endpoint: '/productions/api/v1/production/view/translationcomponent',
      description:
        'Get translation component configuration including file types, TIFF slip sheet settings, and translation file groups',
      status: 'available',
    },
    {
      path: '/demoproductionapi/manage-templates',
      label: 'Manage Templates',
      method: 'GET',
      endpoint: '/productions/api/v1/production/templates',
      description:
        'Get list of production templates with pagination and sorting',
      status: 'available',
    },
    {
      path: '/demoproductionapi/production-basic-info',
      label: 'Production Basic Info',
      method: 'GET',
      endpoint: '/productions/api/v1/production/view/basicinformation',
      description:
        'Get basic information for a production including templates and settings',
      status: 'available',
    },
    {
      path: '/demoproductionapi/security-groups',
      label: 'Security Groups',
      method: 'GET',
      endpoint: '/productions/api/v1/production/view/home',
      description: 'Get security groups and default view settings',
      status: 'available',
    },
    {
      path: '/demoproductionapi/productions-list',
      label: 'Productions List',
      method: 'GET',
      endpoint: '/productions/api/v1/production/view/renderproductiontiles',
      description: 'Get list of production tiles with filtering and pagination',
      status: 'available',
    },
    {
      path: '/demoproductionapi/production-set-info',
      label: 'Production Set Info',
      method: 'GET',
      endpoint:
        '/productions/api/v1/production/view/renderproductionsetinfo?securityGrpId={id}&setId={id}&selectedView={view}',
      description:
        'Fetch production/export set metadata including counts, owner, and created date',
      status: 'available',
    },
    {
      path: '/demoproductionapi/exports-list',
      label: 'Exports List',
      method: 'GET',
      endpoint: '/productions/api/v1/production/view/renderexporttiles',
      description: 'Get list of export tiles with filtering and pagination',
      status: 'available',
    },
    {
      path: '/demoproductionapi/create-set',
      label: 'Create Production/Export Set',
      method: 'POST',
      endpoint: '/productions/api/v1/production/sets',
      description: 'Create a new production or export set',
      status: 'available',
    },
    {
      path: '/demoproductionapi/update-set',
      label: 'Update Production/Export Set',
      method: 'PUT',
      endpoint: '/productions/api/v1/production/sets/update',
      description:
        'Update details for an existing production or export set (name, type, security group)',
      status: 'available',
    },
    {
      path: '/demoproductionapi/save-basic-info',
      label: 'Save Production Basic Info',
      method: 'POST',
      endpoint: '/productions/api/v1/production/basic-info',
      description: 'Save or update production basic information',
      status: 'available',
    },
    {
      path: '/demoproductionapi/delete-template',
      label: 'Delete Template',
      method: 'DELETE',
      endpoint: '/productions/api/v1/production/templates/delete/{id}',
      description: 'Delete a production template by ID',
      status: 'available',
    },
    {
      path: '/demoproductionapi/delete-set',
      label: 'Delete Production/Export Set',
      method: 'DELETE',
      endpoint: '/productions/api/v1/production/sets/delete',
      description:
        'Delete an existing production or export set by specifying security group, project, and set identifiers',
      status: 'available',
    },
    {
      path: '/demoproductionapi/create-template',
      label: 'Create Production Template',
      method: 'POST',
      endpoint: '/productions/api/v1/production/templates',
      description:
        'Create a custom production template from an existing production',
      status: 'available',
    },
    {
      path: '/demoproductionapi/delete-production',
      label: 'Delete Production',
      method: 'DELETE',
      endpoint: '/productions/api/v1/production/delete/{productionId}',
      description: 'Delete a production by its encrypted production ID',
      status: 'available',
    },
    {
      path: '/demoproductionapi/upload-file',
      label: 'Upload File',
      method: 'POST',
      endpoint: '/productions/api/v1/production/upload/file',
      description:
        'Upload a file for production processing (supports Excel, PDF, and other document formats)',
      status: 'available',
    },
    {
      path: '/demoproductionapi/numbering-sorting-view',
      label: 'Numbering & Sorting View',
      method: 'GET',
      endpoint: '/productions/api/v1/production/view/numberingsortingview',
      description:
        'Get numbering and sorting configuration including sort order options, tags list, and production settings',
      status: 'available',
    },
    {
      path: '/demoproductionapi/numbering-sorting-save',
      label: 'Save Numbering & Sorting',
      method: 'POST',
      endpoint: '/productions/api/v1/production/numbering-sorting',
      description:
        'Save numbering and sorting settings for production including numbering settings, sorting settings, and mark complete status',
      status: 'available',
    },
    {
      path: '/demoproductionapi/bates-next',
      label: 'Get Next Bates Numbers',
      method: 'GET',
      endpoint: '/productions/api/v1/production/bates/next',
      description:
        'Get the next available Bates numbers for production including prefix, suffix, and minimum length',
      status: 'available',
    },
    {
      path: '/demoproductionapi/download-production',
      label: 'Download Production',
      method: 'GET',
      endpoint: '/productions/api/v1/production/downloads',
      description:
        'Download production file (binary content) with query parameters for sourcePath and fileName. Returns ZIP file with content-disposition header.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/native-error-list',
      label: 'Get Native Error List',
      method: 'POST',
      endpoint: '/productions/api/v1/production/errors/native-list',
      description:
        'Get list of native production errors with pagination, sorting, and filtering. Returns error details including document IDs, error messages, and export status.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/error-list',
      label: 'Get Error List',
      method: 'POST',
      endpoint: '/productions/api/v1/production/errors/list',
      description:
        'Get list of production errors with pagination, sorting, and filtering. Returns error details including document IDs, error messages, and export status.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/error-export',
      label: 'Export Error List',
      method: 'GET',
      endpoint: '/productions/api/v1/production/errors/export',
      description:
        'Export production errors. GET request with query parameters (productionId, SecurityGroupID, ModuleType). Returns a message indicating that generation has started successfully.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/product-location-view',
      label: 'Get Product Location View',
      method: 'GET',
      endpoint: '/productions/api/v1/production/view/productlocationview',
      description:
        'Get product location view with production output location settings, folder details, root paths, volume settings, and archive options.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/validate-destination',
      label: 'Validate Destination',
      method: 'GET',
      endpoint: '/productions/api/v1/production/destination/validate',
      description:
        'Validate a production root path. Returns success status, whether the path already exists, and a descriptive message.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/create-destination',
      label: 'Create Destination',
      method: 'POST',
      endpoint: '/productions/api/v1/production/destination',
      description:
        'Create a production destination folder. POST request with query parameter (rootPath) and request body (RootPath, DestinationFolder). Returns success status and message.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/save-production-location',
      label: 'Save Production Location',
      method: 'POST',
      endpoint: '/productions/api/v1/production/location',
      description:
        'Save production location settings with output location configuration, folder details, and component settings. Returns a success message.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/summary-view',
      label: 'Get Summary View',
      method: 'GET',
      endpoint: '/productions/api/v1/production/view/summaryview',
      description:
        'Get production summary view with comprehensive statistics including document counts, page counts, status information, and production metrics.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/save-summary',
      label: 'Save Summary (Mark Complete)',
      method: 'POST',
      endpoint: '/productions/api/v1/production/summary',
      description:
        'Mark production as complete. POST request with request body (productionId, securityGroupId, isMarkingComplete). Returns a success message.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/generate-view',
      label: 'Get Generate View',
      method: 'GET',
      endpoint: '/productions/api/v1/production/view/generateview',
      description:
        'Get production generate view with automated check results, generation status, folder paths, and production details.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/generate-status',
      label: 'Get Generate Status',
      method: 'GET',
      endpoint: '/productions/api/v1/production/generate/status',
      description:
        'Get current production generation status with status text, state, progress counts, and completion information.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/export-bates-range',
      label: 'Export Bates Range',
      method: 'GET',
      endpoint: '/productions/api/v1/production/export/bates-range',
      description:
        'Export bates range for a production. GET request with query parameter (productionId). Returns a message indicating that generation has started successfully.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/execute-generate',
      label: 'Execute Generate',
      method: 'POST',
      endpoint: '/productions/api/v1/production/generate/execute',
      description:
        'Execute production generation. POST request with request body containing production details, folder paths, and generation settings. Returns a message indicating that generation has started successfully.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/confirm-production',
      label: 'Confirm Production (Commit/Uncommit)',
      method: 'POST',
      endpoint: '/productions/api/v1/production/confirm',
      description:
        'Commit or uncommit a production. POST request with query parameters (ProductionId, SecurityGroupId, ProductionLabel, IsCommit) and request body containing production details. Returns a message indicating that the action has started.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/shareable-links',
      label: 'Get Shareable Links',
      method: 'GET',
      endpoint: '/productions/api/v1/production/shareable-links',
      description:
        'Get shareable links for a production. GET request with query parameters (productionId, expiryDate, isRegenerate) and request body containing production details. Returns URLs and password.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/confirm-view',
      label: 'Get Confirm View',
      method: 'GET',
      endpoint: '/productions/api/v1/production/confirm-view',
      description:
        'Get production confirm view with QC result data list, production status, and commit/uncommit information.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/production-preview',
      label: 'Get Production Preview',
      method: 'GET',
      endpoint: '/productions/api/v1/production/preview/{productionId}',
      description:
        'Get production preview file information including file existence and file path.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/production-preview-file',
      label: 'Download Production Preview File',
      method: 'GET',
      endpoint: '/productions/api/v1/production/preview/file',
      description:
        'Download production preview file (binary content) with query parameter filePath. Returns file with content-disposition and content-type headers.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/upload-seed-file',
      label: 'Upload Seed File',
      method: 'POST',
      endpoint: '/productions/api/v1/production/upload/seed-file',
      description:
        'Upload a seed file for production processing. Returns the uploaded file path and a success message.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/validate-download',
      label: 'Validate Download',
      method: 'POST',
      endpoint: '/productions/api/v1/production/download/validate-download',
      description:
        'Validate download link and download production file. Requires key, passcode, type, IP address, and absolute URI. Returns binary file.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/get-seed-path',
      label: 'Get Seed Path',
      method: 'GET',
      endpoint: '/productions/api/v1/production/seed-path',
      description:
        'Get the seed file path for a specific document in a production. Requires documentId and productionId as query parameters. Returns success status, seed file path, and page count.',
      status: 'available',
    },
    {
      path: '/demoproductionapi/seed-page-count',
      label: 'Seed Page Count',
      method: 'POST',
      endpoint: '/productions/api/v1/production/seed-page-count',
      description:
        'Save seed page count and update seed file path for a document in a production. Requires pagecount, seedFilePath, newSeedFilePath, documentID, and productionID. Returns success status and message.',
      status: 'available',
    },
  ];

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'GET':
        return 'primary';
      case 'POST':
        return 'success';
      case 'PUT':
        return 'warning';
      case 'DELETE':
        return 'error';
      default:
        return 'default';
    }
  };

  const availableApis = apiEndpoints.filter(
    (api) => api.status === 'available'
  );
  const comingSoonApis = apiEndpoints.filter(
    (api) => api.status === 'coming-soon'
  );

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          🧪 Production API Testing Suite
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Interactive API testing and exploration for Production endpoints
        </Typography>
      </Box>

      {/* Available APIs */}
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h5"
          className="typo-text headline3_size"
          gutterBottom
          sx={{marginBottom: 2}}
        >
          Available APIs ({availableApis.length})
        </Typography>

        <Box sx={{display: 'grid', gap: 2}}>
          {availableApis.map((api) => (
            <Card
              key={api.path}
              sx={{
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                },
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: 1,
                  }}
                >
                  <Box sx={{flex: 1}}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        marginBottom: 1,
                      }}
                    >
                      <Chip
                        label={api.method}
                        color={getMethodColor(api.method)}
                        size="small"
                        sx={{fontWeight: 'bold'}}
                      />
                      <Typography
                        variant="h6"
                        className="typo-text subhead2_size"
                      >
                        {api.label}
                      </Typography>
                    </Box>

                    <Typography
                      className="typo-text body3_size"
                      sx={{
                        fontFamily: 'monospace',
                        backgroundColor: '#f5f5f5',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        display: 'inline-block',
                        marginBottom: 1,
                        fontSize: '12px',
                      }}
                    >
                      {api.endpoint}
                    </Typography>

                    <Typography
                      className="typo-text body3_size"
                      sx={{color: '#666', marginBottom: 2}}
                    >
                      {api.description}
                    </Typography>

                    <Link
                      to={api.path}
                      className="link"
                      style={{
                        textDecoration: 'none',
                        color: '#1976d2',
                        fontWeight: 500,
                      }}
                    >
                      🚀 Test this API →
                    </Link>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      {/* Coming Soon */}
      {comingSoonApis.length > 0 && (
        <Box>
          <Typography
            variant="h5"
            className="typo-text headline3_size"
            gutterBottom
            sx={{marginBottom: 2}}
          >
            Coming Soon ({comingSoonApis.length})
          </Typography>

          <Box sx={{display: 'grid', gap: 2}}>
            {comingSoonApis.map((api) => (
              <Card key={api.path} sx={{opacity: 0.6}}>
                <CardContent>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <Chip label={api.method} color="default" size="small" />
                    <Typography
                      variant="h6"
                      className="typo-text subhead2_size"
                    >
                      {api.label}
                    </Typography>
                    <Chip label="Coming Soon" color="default" size="small" />
                  </Box>
                  <Typography
                    className="typo-text body3_size"
                    sx={{marginTop: 1, color: '#666'}}
                  >
                    {api.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}

      {/* API Info */}
      <Box
        sx={{
          marginTop: 4,
          padding: 2,
          backgroundColor: '#f5f5f5',
          borderRadius: 1,
        }}
      >
        <Typography className="typo-text subhead3_size" gutterBottom>
          📚 API Information
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Base URL:</strong>{' '}
          https://arr-api-productions-dev.us2kd2w30.k8d.consiliotest.com/
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Authentication:</strong> Bearer token (automatically added via
          interceptors)
        </Typography>
        <Typography className="typo-text body3_size">
          <strong>Session Header:</strong> X-Dev-Session (automatically added)
        </Typography>
      </Box>
    </Box>
  );
};

export default DemoApiProductionList;
